import { getAudioContext } from '../audio/Context';
import Recorder, { RecorderEvent } from './Recorder';
import { formatBytes, isElectronApp, postMessageToIosWrapper } from '../utils';
import WrapperInterface from '../utils/WrapperInterface';
import FileType from '../data/FileType';
import RecordType from '../data/RecordType';
import { Soundtrack } from '../audio/Soundtrack';
import {
  convertVideo,
  deleteFile,
  getAvailableFilenameWithExtension,
  getBaseURL,
  renameFile,
  uploadFileCancellable
} from '../utils/S3Utils';
import { ManagedUpload } from 'aws-sdk/clients/s3';
import {
  generateConversionParams,
  MediaConvertNameModifier
} from '../utils/AWSConfig';
import { waitForJobCompletion } from '../utils/AWSMediaConvertUtils';
import { getStoreState } from '../state/Store';
import { reportBFError } from 'utils/Tracker';
import { callFunction } from 'utils/FirebaseUtils';
import { MAX_LAMBDA_VIDEO_SIZE, VIDEO_FPS } from 'data/Config';
import { PlatformType } from 'data/PlatformType';

const MediaRecorder = (window as any).MediaRecorder;
const MediaStream = (window as any).MediaStream;

// const H264 = 'video/webm;codecs=h264';
// const VP9 = 'video/webm;codecs=vp9';
const VP8 = 'video/webm';
const MP4 = 'video/mp4';

const mimeType = MediaRecorder?.isTypeSupported(VP8) ? VP8 : MP4;

const RECORDING_OPTIONS = {
  type: 'video',
  mimeType,
  audioBitsPerSecond: 128 * 1000,
  videoBitsPerSecond: (mimeType === MP4 ? 12 : 150) * 1000 * 1000
};

export default class extends Recorder {
  recorder: any;
  recordGainNode: GainNode | null;
  recordedChunks: Blob[];
  videoStream: MediaStream | null;
  duration: number;
  startTime: number | undefined = undefined;
  type = RecordType.VideoLive;
  soundtrack: Soundtrack | null;

  private fileId: string;
  private videoUploadRequest: ManagedUpload | undefined = undefined;
  private videoUrl: string;
  private hasUploadedVideo = false;
  private jobId: string | undefined;
  private framesRecorded = 0;
  private framesRecordingInt = 0;

  constructor(
    canvas: HTMLCanvasElement,
    soundtrack: Soundtrack | null,
    duration: number
  ) {
    super();

    this.soundtrack = soundtrack;
    this.duration = duration;

    const isMobileApp = getStoreState().app.platform === PlatformType.iOSApp;

    if (isMobileApp) {
      postMessageToIosWrapper('startRecording', {
        width: `${canvas.width}`,
        height: `${canvas.height}`,
        mode: 'live'
      });
    }

    const videoStream = new MediaStream();
    const canvasStream = canvas.captureStream(VIDEO_FPS);
    canvasStream.getVideoTracks().forEach(track => {
      videoStream.addTrack(track);
    });

    if (soundtrack) {
      const audioStream = soundtrack.getAudioStream();
      var ctx = getAudioContext();
      var src = ctx.createMediaStreamSource(audioStream);
      var dst = (ctx as any).createMediaStreamDestination();
      this.recordGainNode = ctx.createGain();

      src.connect(this.recordGainNode);
      this.recordGainNode!.connect(dst);
      videoStream.addTrack(dst.stream.getAudioTracks()[0]);
    }
    this.recordedChunks = [];
    this.recorder = new MediaRecorder(videoStream, RECORDING_OPTIONS);
    this.recorder.ondataavailable = (event: any) => {
      console.log(
        `Recorded ${formatBytes(event.data.size)} ${mimeType} video long ${
          duration / 1000
        }s with VideoRecorder`
      );
      if (event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };
    this.recorder.onstop = () => {
      if (this.hasCancelled) {
        return;
      }
      const fps = this.framesRecorded / (this.duration / 1000);
      const type = mimeType === MP4 ? FileType.mp4 : FileType.webm;
      const blob = new Blob(this.recordedChunks, {
        type
      });
      if (type === FileType.mp4) {
        this.emit(RecorderEvent.Complete, URL.createObjectURL(blob), fps);
      } else if (isElectronApp()) {
        // storeDispatch({
        //   type: Actions.STARTED_VIDEO_CONVERSION
        // });
        const wi: WrapperInterface = (window as any).beatflyerWrapperInterface;
        wi.compressVideo(
          blob,
          this.duration,
          this.conversionDidProgress,
          30
        ).then((compressedBlob: Blob) => {
          this.emit(
            RecorderEvent.Complete,
            URL.createObjectURL(compressedBlob)
          );
        });
      } else {
        if (getStoreState().app.isBeatflyerLite) {
          this.emit(RecorderEvent.Complete, URL.createObjectURL(blob));
        } else {
          this.convertInTheCloud(blob);
        }
      }
    };
    this.videoStream = videoStream;
    this.framesRecordingInt = requestAnimationFrame(
      this.increaseRecordedFrames
    );
  }

  increaseRecordedFrames = () => {
    this.framesRecorded++;
    if (this.videoStream !== null) {
      this.framesRecordingInt = requestAnimationFrame(
        this.increaseRecordedFrames
      );
    }
  };

  get waitingForFrames(): boolean {
    return false;
  }

  convertInTheCloud = async (blob: Blob) => {
    this.fileId = await getAvailableFilenameWithExtension('webm');
    const filename = this.fileId + '.webm';
    const file = new File([blob], filename);
    this.videoUploadRequest = uploadFileCancellable(
      filename,
      file,
      FileType.webm,
      () => {
        console.log('Uploaded video');
        this.videoUploadRequest = undefined;
        this.videoUrl = getBaseURL() + filename;
        this.hasUploadedVideo = true;
        this.didUpload(file.size);
      },
      (error: string) => {
        reportBFError(new Error(error));
        alert(error);
      },
      (progress: number) => {
        this.emit(RecorderEvent.UploadProgressed, progress);
      }
    );
  };

  async converVideoWithMediaConvert() {
    const params = generateConversionParams(this.videoUrl);
    this.jobId = (await callFunction('createMediaConvertJob', params)).data;
    console.log('Job Id ' + this.jobId);
    if (!this.jobId) {
      throw new Error('Could not create conversion job');
    }
    const success = await waitForJobCompletion(this.jobId);
    const oldName = `${this.fileId}${MediaConvertNameModifier}.mp4`;
    const newName = `${this.fileId}.mp4`;
    await renameFile(oldName, newName);
    if (success && !this.hasCancelled) {
      this.emit(RecorderEvent.Complete, getBaseURL() + newName);
    }
  }

  async convertVideoWithLambdaFunction() {
    const newVideoUrl = await convertVideo(this.videoUrl, undefined, 30);
    if (!this.hasCancelled) {
      this.emit(RecorderEvent.Complete, newVideoUrl);
    }
  }

  async didUpload(filesize: number) {
    if (this.hasCancelled) {
      return;
    }
    if (this.videoUrl) {
      this.emit(RecorderEvent.ConversionProgressed, undefined);
      try {
        if (filesize > MAX_LAMBDA_VIDEO_SIZE) {
          await this.converVideoWithMediaConvert();
        } else {
          await this.convertVideoWithLambdaFunction();
        }
      } catch (e) {
        if (!this.hasCancelled) {
          alert(e);
        }
      }
      this.deleteUploadedAssets();
    }
  }

  conversionDidProgress = (progress: number) => {
    this.emit(RecorderEvent.ConversionProgressed, progress);
  };

  getPosition(): number {
    if (!this.startTime) {
      return 0;
    }
    return Math.max(
      0,
      Math.min(1, (Date.now() - this.startTime) / this.duration)
    );
  }

  record(fadeIn: boolean) {
    this.setVolume(fadeIn ? 0 : 1);
    this.recorder.start();
    this.startTime = Date.now();
    if (this.soundtrack) {
      this.soundtrack.endCallback = this.recordingEnded;
    } else {
      window.setTimeout(this.recordingEnded, this.duration);
    }
  }
  setVolume(value: number) {
    if (this.recordGainNode) {
      this.recordGainNode.gain.setValueAtTime(
        value,
        getAudioContext().currentTime
      );
    }
  }

  recordingEnded = () => {
    if (this.hasCancelled) {
      return;
    }

    cancelAnimationFrame(this.framesRecordingInt);

    if (this.soundtrack) {
      this.soundtrack.endCallback = null;
      // this.soundtrack.pause();
    }

    this.emit(RecorderEvent.RecordingEnded);

    if (this.recorder.pause) {
      this.recorder.pause();
      setTimeout(() => {
        this.recorder.stop();
      }, 500);
    } else {
      this.recorder.stop();
    }
  };

  abortUploadRequests = () => {
    if (this.videoUploadRequest) {
      this.videoUploadRequest.abort();
    }
  };

  deleteUploadedAssets = async () => {
    if (this.hasUploadedVideo) {
      await deleteFile(this.fileId + '.webm');
    }
  };

  cancel() {
    super.cancel();

    cancelAnimationFrame(this.framesRecordingInt);

    if (this.soundtrack) {
      this.soundtrack.endCallback = null;
    }

    this.abortUploadRequests();
    this.deleteUploadedAssets();
    if (this.jobId) {
      // cancel conversion
      callFunction('cancelMediaConvertJob', { jobId: this.jobId });
    } else {
      if (this.recorder.state === 'recording') {
        // cancel recording
        this.recorder.stop();
      }
    }
  }

  dispose() {
    this.videoStream!.getTracks().forEach(track => {
      track.stop();
      this.videoStream!.removeTrack(track);
    });
    this.videoStream = null;
    this.recordGainNode = null;
  }

  getSpectrum(): Uint8Array {
    if (this.soundtrack) {
      return this.soundtrack.getSpectrum();
    } else {
      return new Uint8Array([]);
    }
  }

  getWaveform(): Uint8Array {
    if (this.soundtrack) {
      return this.soundtrack.getWaveform();
    } else {
      return new Uint8Array([]);
    }
  }
}



// WEBPACK FOOTER //
// ./src/video/VideoRecorder.ts